/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import { MED_DATE_ONLY_DISPLAY, TIME_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Typography2 } from '@coursera/cds-core';
import { SvgEye, SvgHidden, SvgInfo } from '@coursera/coursera-ui/svg';

import {
  getLearnerCommentLabel,
  getPartnerCommentLabel,
} from 'bundles/author-assignment-grading/constants/CommentLabels';
import {
  ITEM_OUTCOME_HIDE_ACTION,
  ITEM_OUTCOME_OVERRIDE_DELETE,
  ITEM_OUTCOME_OVERRIDE_UPDATE,
  ITEM_OUTCOME_UNHIDE_ACTION,
} from 'bundles/author-assignment-grading/constants/ItemOutcomeActions';
import { getGradeActionByTypeName } from 'bundles/author-assignment-grading/utils/ItemOutcomeActionsUtils';
import { getPercentGradeFromDecimal } from 'bundles/author-common/utils/GradeUtils';
import ActionExplanation, { explanationTypes } from 'bundles/authoring/common/components/ActionExplanation';
import StaticModal from 'bundles/authoring/common/modals/StaticModal';
import type { UserItemGrade } from 'bundles/authoring/common/types/gradebookV2';
import { TooltipWrapper } from 'bundles/common/components/Tooltip';

import _t from 'i18n!nls/author-assignment-grading';

import 'css!./__styles__/SaveActionConfirmModal';

type InputProps = {
  sessionId: string;
  grade: UserItemGrade;
  onCancel: () => void;
  onConfirm: () => void;
  isSentToGradebook?: boolean;
  areRubricsComplete?: boolean;
  newGradeFromRubrics?: number;
};

type Props = InputProps & {
  draftCreatorProfileNames: { [userId: string]: string };
};

/**
 * This function is used to show the new grade when there is a change in SGA rubrics
 * @param grade
 * @param newGradeFromRubrics
 */
export const getNewGrade = (grade: UserItemGrade, newGradeFromRubrics?: number) => {
  if (!(grade.liveOutcome && typeof grade.liveOutcome.grade === 'number') || typeof newGradeFromRubrics !== 'number') {
    return '--';
  }
  if (grade.overrideOutcome) {
    return `${grade.liveOutcome.grade}%`;
  }
  return `${getPercentGradeFromDecimal(newGradeFromRubrics)}%`;
};

/**
 * Modal for any actions confirmations in assignment grading
 */
export const SaveActionConfirmModal: React.FC<Props> = (props: Props) => {
  const {
    grade,
    onCancel,
    onConfirm,
    draftCreatorProfileNames,
    isSentToGradebook,
    areRubricsComplete,
    newGradeFromRubrics,
  } = props;

  const hideAction = getGradeActionByTypeName(grade, ITEM_OUTCOME_HIDE_ACTION);
  const unhideAction = getGradeActionByTypeName(grade, ITEM_OUTCOME_UNHIDE_ACTION);
  const overrideAction = getGradeActionByTypeName(grade, ITEM_OUTCOME_OVERRIDE_UPDATE);
  const deleteOverrideAction = getGradeActionByTypeName(grade, ITEM_OUTCOME_OVERRIDE_DELETE);

  const { currentDraftTimestamp } = grade.conflictMetadata || {};

  const isGradeHidden = (grade.isHidden && !unhideAction) || !!hideAction;
  const hasGradeBookOverrideAction = overrideAction || deleteOverrideAction;
  const draftCreatorProfileName = grade.conflictMetadata && draftCreatorProfileNames[grade.conflictMetadata.createdBy];
  const learnerComment = overrideAction?.definition.explanation;
  const staffComment = overrideAction?.definition.partnerExplanation;

  return (
    <StaticModal
      className="rc-SaveActionConfirmModal"
      title={_t('Apply changes to grade')}
      enablePrimaryButton={true}
      onPrimaryButtonClick={onConfirm}
      primaryButtonContents={_t('Apply Changes')}
      onClose={onCancel}
      secondaryButtonContents={_t('Cancel')}
    >
      <div className="body">
        {isSentToGradebook && !hasGradeBookOverrideAction && (
          <ActionExplanation
            type={explanationTypes.WARNING}
            text={
              <FormattedHTMLMessage
                message={_t(
                  'You are about to change the grade from <strong>{originalGrade}</strong> to <strong>{newGrade}</strong>'
                )}
                originalGrade={
                  grade.liveOutcome && typeof grade.liveOutcome.grade === 'number'
                    ? `${grade.liveOutcome.grade}%`
                    : '--'
                }
                newGrade={getNewGrade(grade, newGradeFromRubrics)}
              />
            }
            delimited={false}
          />
        )}
        {!isSentToGradebook && areRubricsComplete && !hasGradeBookOverrideAction && (
          <ActionExplanation
            type={explanationTypes.WARNING}
            text={_t('You are about to submit your grades to the gradebook.')}
          />
        )}
        {overrideAction && (
          <ActionExplanation
            type={explanationTypes.WARNING}
            text={
              <FormattedHTMLMessage
                message={_t(
                  'You are about to apply a grade override from <strong>{originalGrade}</strong> to <strong>{overrideGrade}</strong>'
                )}
                originalGrade={
                  grade.liveOutcome && typeof grade.liveOutcome.grade === 'number'
                    ? `${grade.liveOutcome.grade}%`
                    : '--'
                }
                overrideGrade={
                  typeof overrideAction.definition.grade === 'number' ? `${overrideAction.definition.grade}%` : '--'
                }
              />
            }
            delimited={false}
          />
        )}
        {deleteOverrideAction && (
          <ActionExplanation
            type={explanationTypes.WARNING}
            text={
              <FormattedHTMLMessage
                message={_t(
                  'You are about to delete a grade override. This will change the grade from <strong>{overrideGrade}</strong> to <strong>{originalGrade}</strong>'
                )}
                originalGrade={
                  grade.originalOutcome && typeof grade.originalOutcome.grade === 'number'
                    ? `${grade.originalOutcome.grade}%`
                    : '--'
                }
                overrideGrade={`${(grade.overrideOutcome && grade.overrideOutcome.grade) || 0}%`}
              />
            }
            delimited={false}
          />
        )}
        {!isGradeHidden && [
          <ActionExplanation
            type={explanationTypes.CLARIFICATION}
            text={_t('Learner will receive grade immediately')}
            delimited={false}
          />,
          <ActionExplanation
            type={explanationTypes.CLARIFICATION}
            text={_t('Learner will receive an email notification with a link to view their grade')}
            delimited={false}
          />,
        ]}
        {isGradeHidden && (
          <ActionExplanation
            type={explanationTypes.CLARIFICATION}
            text={_t('Grade overrides and changes won’t be visible to learners until you unhide their grade')}
            delimited={false}
          />
        )}
        {draftCreatorProfileName && (
          <div className="detail changed-by" css={{ margin: '24px 0' }}>
            <div className="changed-by-label">
              <Typography2 variant="subtitleMedium" component="label" css={{ marginRight: 4 }}>
                {_t('CHANGES MADE BY')}
              </Typography2>
              <span className="sub-label">
                <TooltipWrapper message={_t('Timestamp of the most recent change')}>
                  <SvgInfo size={18} suppressTitle />
                </TooltipWrapper>
                <Typography2 variant="bodySecondary" color="supportText" component="span" css={{ marginLeft: 12 }}>
                  {/* @ts-expect-error TODO: currentDraftTimestamp can't be undefined */}
                  {`${formatDateTimeDisplay(currentDraftTimestamp, MED_DATE_ONLY_DISPLAY)} `}
                  <span className="time-display">
                    {/* @ts-expect-error TODO: currentDraftTimestamp can't be undefined */}
                    {formatDateTimeDisplay(currentDraftTimestamp, TIME_ONLY_DISPLAY)}
                  </span>
                </Typography2>
              </span>
            </div>
            <Typography2 component="p">{draftCreatorProfileName}</Typography2>
          </div>
        )}
        {learnerComment && (
          <div className="detail learner-comment" css={{ marginBottom: 24 }}>
            <Typography2 variant="subtitleMedium" component="label">
              {getLearnerCommentLabel()}
            </Typography2>
            <Typography2 component="p">{learnerComment}</Typography2>
          </div>
        )}
        {staffComment && (
          <div className="detail staff-comment" css={{ marginBottom: 24 }}>
            <Typography2 variant="subtitleMedium" component="label">
              {getPartnerCommentLabel()}
            </Typography2>
            <Typography2 component="p">{staffComment}</Typography2>
          </div>
        )}
        <div className="detail grade-visibility">
          <Typography2 variant="subtitleMedium" component="label">
            {_t('GRADE VISIBILITY')}
          </Typography2>
          {isGradeHidden ? (
            <Typography2 component="p">
              <SvgHidden /> {_t('Hide from learner')}
            </Typography2>
          ) : (
            <Typography2 component="p">
              <SvgEye /> {_t('Show to learner')}
            </Typography2>
          )}
        </div>
      </div>
    </StaticModal>
  );
};

export default connectToStores<Props, InputProps>(['GradebookStore'], ({ GradebookStore }, { sessionId }) => {
  const draftCreatorProfileNames = GradebookStore.getDraftCreatorProfileNamesBySession(sessionId) || {};
  return {
    draftCreatorProfileNames,
  };
})(SaveActionConfirmModal);
